import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip,
  PieChart, Pie,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { StatCard } from "@/components/common/StatCard";
import { Field, SelectField } from "@/components/common/Field";
import { Button } from "@/components/ui/button";
import { exportLeadsToExcel } from "@/lib/excel";
import { statusMeta, STATUS_ORDER } from "@/lib/constants";
import { formatINRShort, monthLabel } from "@/lib/format";
import {
  FileSpreadsheet, Car, Store, User, CheckCircle2, Trophy, XCircle,
  FileText, FileCheck2, IndianRupee, ScrollText, Loader2, TrendingUp,
} from "lucide-react";

const STATUS_COLOR = {
  NEW: "hsl(221 83% 48%)", CONTACTED: "hsl(199 89% 38%)", FOLLOW_UP: "hsl(35 92% 38%)",
  NEGOTIATION: "hsl(262 52% 52%)", OFFER: "hsl(178 70% 28%)", DEAL_DONE: "hsl(152 58% 32%)",
  LOST: "hsl(0 72% 48%)",
};

function monthOptions(n = 12) {
  const arr = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const v = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    arr.push({ value: v, label: i === 0 ? `This month · ${monthLabel(v)}` : monthLabel(v) });
  }
  return arr;
}

export default function Reports() {
  const { user } = useAuth();
  const months = monthOptions();
  const [month, setMonth] = useState(months[0].value);
  const [employee, setEmployee] = useState("all");
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // export-only filters
  const [expType, setExpType] = useState("all");
  const [expStatus, setExpStatus] = useState("all");

  useEffect(() => {
    api.get("/users").then((res) => setEmployees(res.data.filter((u) => u.role === "employee")));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { month };
    if (employee !== "all") params.employee = employee;
    api.get("/reports", { params }).then((res) => setData(res.data)).finally(() => setLoading(false));
  }, [month, employee]);

  useEffect(() => { load(); }, [load]);

  const doExport = async () => {
    setExporting(true);
    try {
      const params = { month };
      if (employee !== "all") params.employee = employee;
      if (expType !== "all") params.lead_type = expType;
      if (expStatus !== "all") params.status = expStatus;
      const res = await api.get("/reports/export", { params });
      if (!res.data.rows.length) { toast.warning("No records match the selected filters"); return; }
      const empName = employee === "all" ? "All employees" : (employees.find((e) => e.id === employee)?.full_name || "");
      const fname = exportLeadsToExcel({
        rows: res.data.rows,
        generatedBy: user?.full_name,
        filters: {
          month,
          employeeName: empName,
          leadTypeLabel: expType === "all" ? "All types" : (expType === "dealership" ? "Dealership" : "Individual"),
          statusLabel: expStatus === "all" ? "All statuses" : statusMeta(expStatus).label,
          dealStatusLabel: "All",
        },
      });
      toast.success(`Exported ${res.data.rows.length} rows — ${fname}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const empOpts = [{ value: "all", label: "All employees" }, ...employees.map((e) => ({ value: e.id, label: e.full_name }))];
  const t = data?.team;
  const chartData = data ? STATUS_ORDER.map((s) => ({ name: statusMeta(s).label, key: s, value: data.status_distribution.find((x) => x.status === s)?.count || 0 })) : [];
  const pieData = data ? [
    { name: "Dealership", value: t.dealership_leads, color: "hsl(214 84% 47%)" },
    { name: "Individual", value: t.individual_leads, color: "hsl(262 52% 52%)" },
  ] : [];

  return (
    <div className="animate-fade-in-up space-y-5">
      <PageHeader title="Reports" subtitle={monthLabel(month)} />

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label="Month"><SelectField value={month} onChange={setMonth} options={months} /></Field>
        <Field label="Employee"><SelectField value={employee} onChange={setEmployee} options={empOpts} /></Field>
      </div>

      {loading || !data ? <Loader /> : (
        <>
          {/* Team performance */}
          <section>
            <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Team performance</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
              <StatCard label="Total Leads" value={t.total_leads} icon={Car} tone="brand" />
              <StatCard label="Dealership" value={t.dealership_leads} icon={Store} />
              <StatCard label="Individual" value={t.individual_leads} icon={User} />
              <StatCard label="Dealerships" value={t.dealerships_created} icon={Store} />
              <StatCard label="Follow-ups" value={t.followups} icon={TrendingUp} />
              <StatCard label="Deals Done" value={t.deals_done} icon={CheckCircle2} tone="success" />
              <StatCard label="Completed" value={t.completed_deals} icon={Trophy} tone="success" />
              <StatCard label="Lost" value={t.lost_deals} icon={XCircle} tone="danger" />
              <StatCard label="Conversion" value={`${t.conversion}%`} icon={TrendingUp} tone="brand" />
              <StatCard label="Business" value={formatINRShort(t.business_amount)} icon={IndianRupee} tone="success" />
            </div>
          </section>

          {/* Charts */}
          <section className="grid gap-4 lg:grid-cols-5">
            <div className="rounded-xl border border-border bg-card p-4 shadow-card lg:col-span-3">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Leads by stage</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(215 16% 44%)" }} interval={0} angle={-18} textAnchor="end" height={44} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "hsl(215 16% 44%)" }} />
                  <Tooltip cursor={{ fill: "hsl(214 30% 95%)" }} contentStyle={{ borderRadius: 12, border: "1px solid hsl(214 25% 88%)", fontSize: 12 }} />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                    {chartData.map((d) => <Cell key={d.key} fill={STATUS_COLOR[d.key]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border border-border bg-card p-4 shadow-card lg:col-span-2">
              <h3 className="mb-3 text-sm font-semibold text-foreground">Lead source</h3>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={48} outerRadius={80} paddingAngle={2}>
                    {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid hsl(214 25% 88%)", fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex justify-center gap-4 text-xs">
                {pieData.map((d) => (
                  <span key={d.name} className="inline-flex items-center gap-1.5 text-muted-foreground">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} /> {d.name} ({d.value})
                  </span>
                ))}
              </div>
            </div>
          </section>

          {/* Processing */}
          <section>
            <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Deal processing pending</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatCard label="Documents" value={data.processing.documents_pending} icon={FileText} tone="warning" />
              <StatCard label="Approval" value={data.processing.file_approval_pending} icon={FileCheck2} tone="warning" />
              <StatCard label="Payment" value={data.processing.payment_pending} icon={IndianRupee} tone="danger" />
              <StatCard label="COD" value={data.processing.cod_pending} icon={ScrollText} />
            </div>
          </section>

          {/* Employee performance */}
          <section>
            <h2 className="mb-2.5 text-sm font-semibold text-muted-foreground">Employee performance</h2>
            <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
              <div className="grid grid-cols-12 gap-2 border-b border-border bg-secondary/50 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <span className="col-span-4">Employee</span>
                <span className="col-span-2 text-center">Leads</span>
                <span className="col-span-2 text-center">Deals</span>
                <span className="col-span-2 text-center">Conv.</span>
                <span className="col-span-2 text-right">Business</span>
              </div>
              {data.employees.map((p) => (
                <div key={p.employee_id} className="grid grid-cols-12 items-center gap-2 border-b border-border px-4 py-3 text-sm last:border-0">
                  <span className="col-span-4 truncate font-medium text-foreground">{p.employee_name}</span>
                  <span className="col-span-2 text-center tnum">{p.leads_created}</span>
                  <span className="col-span-2 text-center tnum text-success">{p.deals_done}</span>
                  <span className="col-span-2 text-center tnum text-brand">{p.conversion}%</span>
                  <span className="col-span-2 text-right tnum">{formatINRShort(p.business_amount)}</span>
                </div>
              ))}
              {!data.employees.length && <p className="px-4 py-6 text-center text-sm text-muted-foreground">No data for this period.</p>}
            </div>
          </section>

          {/* Excel export */}
          <section className="rounded-xl border border-border bg-card p-4 shadow-card">
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-success/10 text-success"><FileSpreadsheet className="h-4 w-4" /></span>
              <div>
                <h3 className="text-sm font-bold text-foreground">Export to Excel</h3>
                <p className="text-xs text-muted-foreground">Download a clean, formatted report for {monthLabel(month)}.</p>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Field label="Lead type">
                <SelectField value={expType} onChange={setExpType}
                  options={[{ value: "all", label: "All types" }, { value: "dealership", label: "Dealership" }, { value: "individual", label: "Individual" }]} />
              </Field>
              <Field label="Lead status">
                <SelectField value={expStatus} onChange={setExpStatus}
                  options={[{ value: "all", label: "All statuses" }, ...STATUS_ORDER.map((s) => ({ value: s, label: statusMeta(s).label }))]} />
              </Field>
            </div>
            <Button variant="success" size="lg" className="mt-3 w-full" onClick={doExport} disabled={exporting}>
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Export Excel
            </Button>
          </section>
        </>
      )}
    </div>
  );
}
